import { z } from 'zod';

export const baseInputOptionsSchema = z.object({
  id: z.string(),
  order: z.number(),
  name: z.string(),
  label: z.string(),
  required: z.boolean(),
  conditional: z.string().optional(),
});

export const workingBaseInputOptionsSchema = baseInputOptionsSchema.partial({
  name: true,
  label: true,
});

export const textFormFieldSchema = z.object({
  inputType: z.literal('text'),
  placeholder: z.string().optional(),
});

export const telFormFieldSchema = z.object({
  inputType: z.literal('tel'),
  placeholder: z.string().optional(),
});

export const emailFormFieldSchema = z.object({
  inputType: z.literal('email'),
  placeholder: z.string().optional(),
  disallowCommonEmails: z.boolean().optional(),
});

export const selectFormFieldSchema = z.object({
  inputType: z.literal('select'),
  selectOptions: z.string().transform((value) =>
    value.split('|').map((value) => ({
      label: value.trim(),
      value: value.trim(),
    }))
  ),
  placeholder: z.string().optional(),
});

export const workingSelectFormFieldSchema = z.object({
  inputType: z.literal('select'),
  selectOptions: z.string().optional(),
  placeholder: z.string().optional(),
});

export const checkboxFormFieldSchema = z.object({
  inputType: z.literal('checkbox'),
});

export const workingRadioFormFieldSchema = z.object({
  inputType: z.literal('radio'),
  options: z.string().optional(),
  placeholder: z.string().optional(),
});

export const radioFormFieldSchema = z.object({
  inputType: z.literal('radio'),
  options: z.string().transform((value) =>
    value.split('|').map((value) => ({
      label: value.trim(),
      value: value.trim(),
    }))
  ),
});

export const emptyFormFieldSchema = z.object({
  inputType: z.undefined(),
});

export const dateFormFieldSchema = z.object({
  inputType: z.literal('date'),
  placeholder: z.string().optional(),
});

export const linkedInUrlFormFieldSchema = z.object({
  inputType: z.literal('linkedin-profile-url'),
  placeholder: z.string().optional(),
});

export const workingFormFieldSchema = z.union([
  textFormFieldSchema.merge(workingBaseInputOptionsSchema),
  telFormFieldSchema.merge(workingBaseInputOptionsSchema),
  emailFormFieldSchema.merge(workingBaseInputOptionsSchema),
  workingSelectFormFieldSchema.merge(workingBaseInputOptionsSchema),
  checkboxFormFieldSchema.merge(workingBaseInputOptionsSchema),
  workingRadioFormFieldSchema.merge(workingBaseInputOptionsSchema),
  emptyFormFieldSchema.merge(workingBaseInputOptionsSchema),
  dateFormFieldSchema.merge(workingBaseInputOptionsSchema),
  linkedInUrlFormFieldSchema.merge(workingBaseInputOptionsSchema),
]);

export const formFieldSchema = z.union([
  textFormFieldSchema.merge(baseInputOptionsSchema),
  telFormFieldSchema.merge(baseInputOptionsSchema),
  emailFormFieldSchema.merge(baseInputOptionsSchema),
  selectFormFieldSchema.merge(baseInputOptionsSchema),
  checkboxFormFieldSchema.merge(baseInputOptionsSchema),
  radioFormFieldSchema.merge(baseInputOptionsSchema),
  dateFormFieldSchema.merge(baseInputOptionsSchema),
  linkedInUrlFormFieldSchema.merge(baseInputOptionsSchema),
]);

export const formFieldsSchema = z.array(formFieldSchema);

export type FormField = z.infer<typeof formFieldSchema>;
export type FormFields = z.infer<typeof formFieldsSchema>;
export type WorkingFormField = z.infer<typeof workingFormFieldSchema>;
export type TextFormField = z.infer<typeof textFormFieldSchema>;
export type TelFormField = z.infer<typeof telFormFieldSchema>;
export type EmailFormField = z.infer<typeof emailFormFieldSchema>;
export type SelectFormField = z.infer<typeof selectFormFieldSchema>;
export type WorkingSelectFormField = z.infer<typeof workingSelectFormFieldSchema>;
export type CheckboxFormField = z.infer<typeof checkboxFormFieldSchema>;
export type WorkingRadioFormField = z.infer<typeof workingRadioFormFieldSchema>;
export type RadioFormField = z.infer<typeof radioFormFieldSchema>;
export type DateFormField = z.infer<typeof dateFormFieldSchema>;
export type LinkedInUrlFormField = z.infer<typeof linkedInUrlFormFieldSchema>;
